import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { Settings2, Check } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  NOTIFICATION_CATEGORY_LABELS,
  notificationDefinition,
  type NotificationCategory,
  type NotificationRow,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, FilteredEmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDateTimeLong, formatRelative } from '../../../lib/format';
import { useModules } from '../../../lib/modules';
import { liveCategories } from '../live-categories';
import { DeepLink } from '../components/DeepLink';
import { useInbox, useMarkRead } from '../api/notifications-api';
import styles from './NotificationInboxPage.module.css';

/**
 * NOTIF-01 — the notification inbox.
 *
 * Every row is a real anchor to a real record, because that is the whole point of
 * the screen (non-negotiable #3): a notification that lands on a dashboard makes
 * the recipient hunt, and they stop responding.
 *
 * **Opening a row marks it read on the way out**, optimistically, in the same
 * gesture as the navigation. Asking somebody to tick a box after reading something
 * is a second click for information the system already has.
 *
 * `?focus=` is how a purely informational notification — one §L22 gives no deep
 * link — points at itself. The row it names is highlighted rather than scrolled to:
 * the list is short, and a jump on load is disorienting.
 */

/** The route's own typed search, so the page needs no assertion (as in DemoTable). */
const routeApi = getRouteApi('/app-layout/notifications');

export interface NotificationInboxSearch {
  readonly filter?: 'unread';
  readonly category?: NotificationCategory;
  readonly focus?: string;
}

export function NotificationInboxPage(): ReactNode {
  const search = routeApi.useSearch();
  const navigate = useNavigate();
  const markRead = useMarkRead();

  const modules = useModules();
  const categories = liveCategories(modules);
  // A category whose module is off has nothing to show and is not offered (§L28), even from an old link.
  const category = categories.find((live) => live === search.category);

  const filter = search.filter === 'unread' ? 'unread' : 'all';
  const inbox = useInbox({ filter, ...(category ? { category } : {}) });

  const rows = inbox.data?.rows ?? [];
  const unreadCount = inbox.data?.unreadCount ?? 0;
  // D96: a list that never loaded is not an empty one.
  const isFailed = inbox.isError && inbox.data === undefined;
  const activeFilters = (filter === 'unread' ? 1 : 0) + (category ? 1 : 0);

  const categoryOptions = [
    { value: '', label: 'All categories' },
    ...categories.map((live) => ({ value: live, label: NOTIFICATION_CATEGORY_LABELS[live] })),
  ];

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Notifications</h1>
          {!isFailed && (
            <p className={styles.meta}>
              {unreadCount === 0 ? 'Everything here has been read' : `${unreadCount} unread of ${inbox.data?.total ?? 0}`}
            </p>
          )}
        </div>
        <div className={styles.headerActions}>
          <Link to="/notifications/preferences" className={styles.settingsLink}>
            <Settings2 size={14} aria-hidden />
            Settings
          </Link>
          <Button
            variant="outline"
            size="sm"
            icon={Check}
            disabled={unreadCount === 0}
            isPending={markRead.isPending}
            onClick={() => markRead.mutate({})}
          >
            Mark all read
          </Button>
        </div>
      </header>

      <Card>
        <div className={styles.filters}>
          <div className={styles.toggle} role="group" aria-label="Show">
            <Link
              to="/notifications"
              search={{ ...(category ? { category } : {}) }}
              className={styles.toggleItem}
              aria-current={filter === 'all' ? 'true' : undefined}
            >
              All
            </Link>
            <Link
              to="/notifications"
              search={{
                filter: 'unread',
                ...(category ? { category } : {}),
              }}
              className={styles.toggleItem}
              aria-current={filter === 'unread' ? 'true' : undefined}
            >
              {unreadCount > 0 ? `Unread (${unreadCount})` : 'Unread'}
            </Link>
          </div>

          <Field label="Category">
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Category"
                value={category ?? ''}
                onChange={(value) => {
                  const nextCategory = categories.find((live) => live === value);
                  void navigate({
                    to: '/notifications',
                    search: {
                      ...(filter === 'unread' ? { filter: 'unread' as const } : {}),
                      ...(nextCategory ? { category: nextCategory } : {}),
                    },
                  });
                }}
                options={categoryOptions}
              />
            )}
          </Field>
        </div>

        {inbox.isPending ? (
          <div className={styles.loading}>
            <SkeletonLines count={6} />
          </div>
        ) : isFailed ? (
          <ServerErrorState onRetry={() => void inbox.refetch()} />
        ) : rows.length === 0 ? (
          activeFilters > 0 ? (
            <FilteredEmptyState
              filterCount={activeFilters}
              onClearFilters={() => void navigate({ to: '/notifications', search: {} })}
              isInset
            />
          ) : (
            <EmptyState
              title="Nothing yet"
              body="You will be told here when a scorecard is due, an approval needs you, or an SLA is close to breaching."
              action={
                <Link to="/notifications/preferences" className={styles.settingsLink}>
                  <Settings2 size={14} aria-hidden />
                  Choose what reaches you
                </Link>
              }
              isInset
            />
          )
        ) : (
          <ul className={styles.list}>
            {rows.map((row) => (
              <NotificationEntry
                key={row.id}
                row={row}
                isFocused={row.id === search.focus}
                onOpen={() => {
                  if (row.readAt === null) markRead.mutate({ ids: [row.id] });
                }}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function NotificationEntry({
  row,
  isFocused,
  onOpen,
}: {
  readonly row: NotificationRow;
  readonly isFocused: boolean;
  readonly onOpen: () => void;
}): ReactNode {
  const isEscalation =
    notificationDefinition(row.notificationKey)?.isEscalation === true || row.escalationLevel > 0;

  const classes = [
    styles.entry,
    row.readAt === null ? styles.unread : '',
    isFocused ? styles.focused : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={classes}>
      <DeepLink to={row.deepLinkUrl} className={styles.entryLink} onNavigate={onOpen}>
        <span className={styles.entryMain}>
          <span className={styles.entryTitle}>
            {row.readAt === null && <span className={styles.dot} aria-hidden />}
            {row.title}
          </span>
          {row.body !== null && <span className={styles.entryBody}>{row.body}</span>}
        </span>

        <span className={styles.entryMeta}>
          {isEscalation && (
            <Chip tone="danger" emphasis="tonal">
              Escalation
            </Chip>
          )}
          <span className={styles.entryTime} title={formatDateTimeLong(row.createdAt)}>
            {formatRelative(row.createdAt)}
          </span>
        </span>
      </DeepLink>
    </li>
  );
}
